import React, { useState, useEffect } from "react"
import { ButtonGroup, Modal } from "react-bootstrap"
import { Formik } from "formik"
import * as Yup from "yup"
import {
  CRUDLayout,
  InputSearch,
  ExportButton,
  CreateButton,
  DeleteButton,
  UpdateButton,
  ActionButton,
  DataStatus,
  CreateModal,
  UpdateModal,
  DeleteModal,
  Alert,
  Input,
  Pagination,
  Select,
  Switch,
  THead,
  TBody,
  ThFixed,
  TdFixed,
  Tr,
  Th,
  Td,
} from "../../../../components"
import { HirarkiApi } from "../../../../api"
import { TableNumber } from "../../../../utilities"

const JenisAnggaran = ({ setNavbarTitle }) => {
  // Title
  const title = "Hirarki Unit Organisasi"

  // indikator pemanggilan data sedang dimuat di server
  const [isLoading, setIsLoading] = useState(true)

  // indikator apakah sedang melakukan searching
  const [isSearching, setIsSearching] = useState(false)

  // menampung value dari search form
  const [searchKey, setSearchKey] = useState("")

  // menangani modal form tambah
  const [isCreateForm, setIsCreateForm] = useState(false)
  // menangani modal form ubah
  const [isUpdateform, setIsUpdateform] = useState(false)
  // menangani modal hapus data
  const [isDeleteData, setIsDeleteData] = useState(false)

  // data jenis anggaran
  const [data, setData] = useState([])
  const [page, setPage] = useState(1)
  const [totalPage, setTotalPage] = useState(1)
  const [dataLength, setDataLength] = useState(10)
  const [dataCount, setDataCount] = useState(0)

  // menampung data yang akan diubah
  const [updateData, setUpdateData] = useState([])
  // menampung data yang akan dihapus
  const [deleteData, setDeleteData] = useState([])

  // menampilkan alert
  const [showAlert, setShowAlert] = useState(false)
  // configurasi alert
  const [alertConfig, setAlertConfig] = useState({
    variant: "primary",
    text: "",
    searchKey: "",
  })

  // request data dari server
  const getData = () => {
    setIsLoading(true)
    setIsSearching(false)

    // request data ke server
    HirarkiApi.getPage(page, dataLength, searchKey)
      .then((res) => {
        setData(res.data.data)
        setTotalPage(res.data.total_page)
        setDataCount(res.data.data_count)
      })
      .catch(() => {
        setAlertConfig({
          variant: "danger",
          text: "Gagal untuk memuat data",
        })
        setShowAlert(true)
      })
      .finally(() => {
        if (searchKey !== "") {
          setAlertConfig({
            variant: "primary",
            text: `Hasil Pencarian : ${searchKey}`,
          })
          setShowAlert(true)
        } else {
          setShowAlert(false)
        }
        setIsLoading(false)
      })
  }

  // request search data dari server
  const searchData = (e) => {
    // mencegah terjadinya page reload
    e.preventDefault()

    // set loading
    setIsLoading(true)

    // nilai dari form search
    const key = searchKey

    HirarkiApi.search(key)
      .then((res) => {
        setData(res.data.data)
        setTotalPage(res.data.total_page)
        setAlertConfig({
          variant: "primary",
          searchKey: key,
        })
      })
      .catch(() => {
        setAlertConfig({
          variant: "danger",
          text: "Gagal untuk memuat data",
        })
      })
      .finally(() => {
        setPage(1)
        setIsSearching(true)
        setIsLoading(false)
        setShowAlert(true)
      })
  }

  // menangani hide/show data
  const changeDataStatus = (status, id) => {
    setIsLoading(true)
    setShowAlert(false)

    const value = {
      id_hirarki_unit_organisasi: id,
    }

    const onLoadedSuccess = () => {
      setIsSearching(false)
      setAlertConfig({
        variant: "primary",
        text: "Ubah status data berhasil",
      })
      setShowAlert(true)
    }

    const onLoadedFailed = () => {
      setIsSearching(false)
      setAlertConfig({
        variant: "danger",
        text: "Ubah status data gagal",
      })
      setShowAlert(true)
    }

    status === true
      ? HirarkiApi.show(value)
        .then(() => onLoadedSuccess())
        .catch(() => onLoadedFailed())
        .finally(() => getData())
      : HirarkiApi.hide(value)
        .then(() => onLoadedSuccess())
        .catch(() => onLoadedFailed())
        .finally(() => getData())
  }

  useEffect(() => {
    // set judul di Navbar
    setNavbarTitle(title)

    // jalankan function request data ke server
    getData()

    // menangani pembersihan saat fetching data
    return () => {
      setIsLoading(false)
      setIsSearching(false)
    }
  }, [setNavbarTitle, page, dataLength, searchKey])

  // modal tambah
  const TambahModal = () => {
    // nilai awal form
    const formInitialValues = {
      level_hirarki_unit_organisasi: "",
      nama_hirarki_unit_organisasi: "",
    }

    // skema validasi form
    const formValidationSchema = Yup.object().shape({
      level_hirarki_unit_organisasi: Yup.number()
        .required("Pilih level hirarki")
        .typeError("Level hanya dapat berupa angka"),
      nama_hirarki_unit_organisasi: Yup.string().required("Masukan nama hirarki"),
    })

    // request tambah data ke server
    const formSubmitHandler = (values) => {
      HirarkiApi.create(values)
        .then(() => {
          // konfigurasi alert
          setAlertConfig({
            variant: "primary",
            text: "Tambah data berhasil!",
          })
        })
        .catch(() => {
          // konfigurasi alert
          setAlertConfig({
            variant: "danger",
            text: `Tambah data gagal!`,
          })
        })
        .finally(() => {
          // menutup modal
          setIsCreateForm(false)
          // menampilkan alert
          setShowAlert(true)
          // request data baru ke server
          getData()
        })
    }

    return (
      <CreateModal show={isCreateForm} onHide={() => setIsCreateForm(false)} title={title}>
        <Formik
          initialValues={formInitialValues}
          validationSchema={formValidationSchema}
          onSubmit={formSubmitHandler}
        >
          {({ values, errors, touched, isSubmitting, handleChange, handleSubmit }) => (
            <form onSubmit={handleSubmit}>
              <Modal.Body>
                <Select
                  label="Level Hirarki"
                  name="level_hirarki_unit_organisasi"
                  onChange={handleChange}
                  error={
                    errors.level_hirarki_unit_organisasi &&
                    touched.level_hirarki_unit_organisasi &&
                    true
                  }
                  errorText={errors.level_hirarki_unit_organisasi}
                >
                  <option value="">Pilih level hirarki</option>
                  {[...Array(10)].map((e, i) => (
                    <option key={i} value={i + 1}>
                      {i + 1}
                    </option>
                  ))}
                </Select>
                <Input
                  label="Nama Hirarki"
                  placeholder="Masukan nama hirarki"
                  name="nama_hirarki_unit_organisasi"
                  value={values.nama_hirarki_unit_organisasi}
                  error={
                    errors.nama_hirarki_unit_organisasi &&
                    touched.nama_hirarki_unit_organisasi &&
                    true
                  }
                  errorText={errors.nama_hirarki_unit_organisasi}
                  onChange={handleChange}
                />
              </Modal.Body>
              <Modal.Footer>
                <ActionButton
                  type="submit"
                  variant="primary"
                  text="Tambah Data"
                  loading={isSubmitting}
                />
              </Modal.Footer>
            </form>
          )}
        </Formik>
      </CreateModal>
    )
  }

  // modal ubah
  const UbahModal = () => {
    // nilai awal form
    const formInitialValues = {
      level_hirarki_unit_organisasi: updateData.level_hirarki_unit_organisasi,
      nama_hirarki_unit_organisasi: updateData.nama_hirarki_unit_organisasi,
    }

    // skema validasi form
    const formValidationSchema = Yup.object().shape({
      level_hirarki_unit_organisasi: Yup.number()
        .required("Pilih level hirarki")
        .typeError("Level hanya dapat berupa angka"),
      nama_hirarki_unit_organisasi: Yup.string().required("Masukan nama hirarki"),
    })

    // request ubah data ke server
    const formSubmitHandler = (values) => {
      const finalValues = {
        id_hirarki_unit_organisasi: updateData.id_hirarki_unit_organisasi,
        ...values,
      }

      HirarkiApi.update(finalValues)
        .then(() => {
          // konfigurasi alert
          setAlertConfig({
            variant: "primary",
            text: "Ubah data berhasil!",
          })
        })
        .catch(() => {
          // konfigurasi alert
          setAlertConfig({
            variant: "danger",
            text: `Ubah data gagal!`,
          })
        })
        .finally(() => {
          // menutup modal
          setIsUpdateform(false)
          // menampilkan alert
          setShowAlert(true)
          // request data baru ke server
          getData()
        })
    }

    return (
      <UpdateModal show={isUpdateform} onHide={() => setIsUpdateform(false)} title={title}>
        <Formik
          initialValues={formInitialValues}
          validationSchema={formValidationSchema}
          onSubmit={formSubmitHandler}
        >
          {({ values, errors, touched, isSubmitting, handleChange, handleSubmit }) => (
            <form onSubmit={handleSubmit}>
              <Modal.Body>
                <Select
                  label="Level Hirarki"
                  name="level_hirarki_unit_organisasi"
                  defaultValue={values.level_hirarki_unit_organisasi}
                  error={
                    errors.level_hirarki_unit_organisasi &&
                    touched.level_hirarki_unit_organisasi &&
                    true
                  }
                  errorText={errors.level_hirarki_unit_organisasi}
                  onChange={handleChange}
                >
                  <option value="">Pilih level hirarki</option>
                  {[...Array(10)].map((e, i) => (
                    <option key={i} value={i + 1}>
                      {i + 1}
                    </option>
                  ))}
                </Select>
                <Input
                  label="Nama Hirarki"
                  placeholder="Masukan nama hirarki"
                  name="nama_hirarki_unit_organisasi"
                  value={values.nama_hirarki_unit_organisasi}
                  error={
                    errors.nama_hirarki_unit_organisasi &&
                    touched.nama_hirarki_unit_organisasi &&
                    true
                  }
                  errorText={errors.nama_hirarki_unit_organisasi}
                  onChange={handleChange}
                />
              </Modal.Body>
              <Modal.Footer>
                <ActionButton
                  type="submit"
                  variant="success"
                  text="Ubah Data"
                  loading={isSubmitting}
                />
              </Modal.Footer>
            </form>
          )}
        </Formik>
      </UpdateModal>
    )
  }

  // modal hapus
  const HapusModal = () => {
    // set id dari data yang ingin dihapus
    const idData = deleteData.id_hirarki_unit_organisasi
    const values = { id_hirarki_unit_organisasi: idData }

    // menangani delete button loading
    const [btnLoading, setBtnLoading] = useState(false)

    // menangani pembersihan saat fetching data
    useEffect(() => {
      return () => {
        setBtnLoading(false)
      }
    }, [])

    // request hapus data ke server
    const deleteDataHandler = () => {
      // set delete button loading
      setBtnLoading(true)

      HirarkiApi.delete(values)
        .then(() => {
          // konfigurasi alert
          setAlertConfig({
            variant: "primary",
            text: "Hapus data berhasil!",
          })
        })
        .catch(() => {
          // konfigurasi alert
          setAlertConfig({
            variant: "danger",
            text: `Hapus data gagal!`,
          })
        })
        .finally(() => {
          // menutup modal
          setIsDeleteData(false)
          // menampilkan alert
          setShowAlert(true)
          // request data baru ke server
          getData()
        })
    }

    return (
      <DeleteModal
        show={isDeleteData}
        onHide={() => setIsDeleteData(false)}
        loading={btnLoading}
        onConfirm={deleteDataHandler}
        title={title}
      >
        <span>Nama Hirarki : {deleteData.nama_hirarki_unit_organisasi}</span>
      </DeleteModal>
    )
  }

  // Tabel
  const Table = () => (
    <>
      <CRUDLayout.Table>
        <THead>
          <Tr>
            <ThFixed>No</ThFixed>
            <ThFixed>Aksi</ThFixed>
            <ThFixed>Level</ThFixed>
            <Th>Nama Hirarki</Th>
          </Tr>
        </THead>
        <TBody>
          {data.map((val, index) => (
            <Tr key={index}>
              <TdFixed>{TableNumber(page, dataLength, index)}</TdFixed>
              <Td>
                <div className="d-flex justify-content-center align-items-center">
                  <ButtonGroup>
                    <UpdateButton
                    noMargin
                      onClick={() => {
                        setUpdateData(val)
                        setIsUpdateform(true)
                      }}
                    />
                    <DeleteButton
                    noMargin
                      onClick={() => {
                        setDeleteData(val)
                        setIsDeleteData(true)
                      }}
                    />
                  </ButtonGroup>

                  <Switch
                    id={toString(index + 1)}
                    checked={!val.is_hidden}
                    onChange={() => changeDataStatus(val.is_hidden, val.id_hirarki_unit_organisasi)}
                  />
                </div>
              </Td>
              <TdFixed>{val.level_hirarki_unit_organisasi}</TdFixed>
              <Td>{val.nama_hirarki_unit_organisasi}</Td>
            </Tr>
          ))}
        </TBody>
      </CRUDLayout.Table>
      {!isSearching && (
        <Pagination
          dataLength={dataLength}
          dataNumber={page * dataLength - dataLength + 1}
          dataPage={dataCount < dataLength ? dataCount : page * dataLength}
          dataCount={dataCount}
          onDataLengthChange={(e) => {
            setDataLength(e.target.value)
            setPage(1)
          }}
          currentPage={page}
          totalPage={totalPage}
          onPaginationChange={({ selected }) => setPage(selected + 1)}
        />
      )}
    </>
  )

  return (
    <CRUDLayout>
      {/* head */}
      <CRUDLayout.Head>
        {/* search section */}
        <CRUDLayout.HeadSearchSection>
          <div className="d-flex mb-3">
            <InputSearch
              onChange={(e) => {
                setTimeout(() => {
                  setSearchKey(e.target.value)
                  setPage(1)
                }, 1000)
              }}
              onSubmit={(e) => e.preventDefault()}
            />
          </div>
        </CRUDLayout.HeadSearchSection>

        {/* button section */}
        <CRUDLayout.HeadButtonSection>
          <CreateButton onClick={() => setIsCreateForm(true)} />
        </CRUDLayout.HeadButtonSection>
      </CRUDLayout.Head>

      {/* alert */}
      {isSearching ? (
        <Alert
          show={showAlert}
          showCloseButton={true}
          variant={alertConfig.variant}
          text={`Hasil dari pencarian: ${alertConfig.searchKey}`}
          onClose={() => {
            setShowAlert(false)
            getData()
            setIsSearching(false)
          }}
        />
      ) : (
        <Alert
          show={showAlert}
          showCloseButton={true}
          variant={alertConfig.variant}
          text={alertConfig.text}
          onClose={() => setShowAlert(false)}
        />
      )}

      {/* table section */}
      {
        // cek apakah data sedang dimuat (loading)
        isLoading === true ? (
          // loading
          <DataStatus loading={true} text="Memuat data..." />
        ) : // Cek apakah ada data
          data.length > 0 ? (
            // Ada data
            <Table />
          ) : (
            // Tidak ada data
            <DataStatus text="Tidak ada data" />
          )
      }

      {/* Modal */}
      <TambahModal />
      <UbahModal />
      <HapusModal />
    </CRUDLayout>
  )
}

export default JenisAnggaran
